
import { useState, useEffect } from "react";
import { MessageCircle, Star } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

// Time to wait before showing feedback request (5 minutes in ms)
const FEEDBACK_DELAY = 5 * 60 * 1000; 

const FeedbackRequestDialog = () => {
  const [feedbackText, setFeedbackText] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Check if user has already given feedback
    const checkFeedbackStatus = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from("feedback")
        .select("id")
        .eq("user_id", user.id)
        .eq("type", "rating")
        .maybeSingle();
      
      // If user hasn't given feedback before, set timeout to show dialog
      if (!data) {
        // Get the user's session start time
        const sessionStartTime = localStorage.getItem('sessionStartTime');
        
        if (!sessionStartTime) {
          // If no session start time, set it now
          localStorage.setItem('sessionStartTime', Date.now().toString());
        } else {
          const elapsed = Date.now() - parseInt(sessionStartTime);
          
          // If enough time has passed, show the feedback dialog
          if (elapsed >= FEEDBACK_DELAY) {
            const timer = setTimeout(() => {
              setIsOpen(true);
            }, 2000); // Small delay after page loads
            
            return () => clearTimeout(timer);
          }
        }
      }
    };
    
    checkFeedbackStatus();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rating) {
      toast.error("Please provide a rating before submitting");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save feedback to Supabase
      const { error } = await supabase.from("feedback").insert({
        user_id: user?.id || null,
        type: "rating",
        message: feedbackText,
        rating: rating
      });
      
      if (error) throw error;
      
      toast.success("Thanks for your feedback!");
      
      // Mark that user has submitted feedback so we don't show again
      localStorage.setItem('feedbackSubmitted', 'true');
      
      // Reset form and close dialog
      setFeedbackText("");
      setRating(null);
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Error submitting feedback. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <Star
          key={index}
          size={32}
          className={`cursor-pointer ${
            (rating || 0) > index 
              ? "fill-yellow-400 text-yellow-400" 
              : "text-gray-300 dark:text-gray-600"
          }`}
          onClick={() => setRating(index + 1)}
        />
      ));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>How are you enjoying Prompt Hub?</DialogTitle>
            <DialogDescription>
              Your feedback helps us improve the platform for everyone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="flex justify-center space-x-2 py-4">
              {renderStars()}
            </div>
            
            <div className="space-y-2">
              <Textarea
                placeholder="Tell us what you think about Prompt Hub (optional)"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Maybe Later
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting || !rating}
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackRequestDialog;
